import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { UserCircle2, Camera, Save } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const UserProfile = () => {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", bio: "", avatar_url: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", phone: user.phone || "",
      bio: user.bio || "", avatar_url: user.avatar_url || "",
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/auth/me", form);
      toast.success("Perfil atualizado");
      if (refresh) await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar_url: data.url }));
      toast.success("Foto atualizada");
      if (refresh) await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro no upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Meu perfil</div>
        <h1 className="font-display font-bold text-3xl text-[#0B2B62] mt-1">{user.name}</h1>
        <p className="text-sm text-slate-500 mt-1">Papel: <b className="text-slate-700">{user.role}</b></p>
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-5">
          <div className="relative">
            {form.avatar_url ? (
              <img data-testid="profile-avatar-img" src={form.avatar_url} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200">
                <UserCircle2 className="w-12 h-12 text-slate-400" />
              </div>
            )}
            <button type="button" data-testid="profile-avatar-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 bg-[#0B2B62] text-white p-2 rounded-full hover:bg-slate-800 disabled:opacity-60">
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadAvatar} data-testid="profile-avatar-input" />
          </div>
          <div className="text-xs text-slate-500">
            {uploading ? "Enviando…" : (user.role === "consultor" ? "Sua foto será exibida às famílias que você atende — humaniza o atendimento." : "Adicione uma foto para personalizar seu perfil.")}
          </div>
        </div>

        <Field label="Nome" testId="profile-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Telefone" testId="profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="(11) 99999-9999" />
        {(user.role === "consultor" || user.role === "admin") && (
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Bio {user.role === "admin" ? "administrativa" : "profissional"}
              <span className="text-slate-400 normal-case tracking-normal ml-1">
                {user.role === "admin"
                  ? "(uso interno — descreva sua função na RAF)"
                  : "(será exibida às famílias)"}
              </span>
            </label>
            <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="profile-bio"
              placeholder={user.role === "admin"
                ? "Ex: Fundador RAF, responsável pela governança clínica e financeira, tomada de decisão em casos críticos..."
                : "Ex: Psicólogo(a), 10 anos atuando com dependência química, especialista em terapia cognitivo-comportamental..."}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
          </div>
        )}

        <button data-testid="profile-save" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0B2B62] hover:bg-slate-800 text-white font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {busy ? "Salvando…" : "Salvar alterações"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
  </div>
);

export default UserProfile;
